import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import axios from "axios";
import {
    FlexContainer,
    Toast,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalDescription,
    StandardModalSection,
    StandardModalTable,
    StandardModalTableScroll,
    StandardModalTableSection
} from "src/components";
import LeadershipRoleModal from "src/scenes/EmployeeList/components/Leadership/LeadershipRoleModal";
import { LeadershipContext } from "src/scenes/EmployeeList/components/Leadership/LeadershipContext";
import { useGetLeadershipRoles } from "src/scenes/Chat/hooks";
import { API_URL } from "src/scenes/App";
import { ViewEditButton,
    CreateRoleButton
} from "../styled";
import { customNames } from "src/utils/helpers";

const ManageLeaderShipRoleModal = ({ showDialog, handleClose, props }) => {

    const headers = { headers: { Authorization: "Bearer " + props.user.token } };
    const { leadershipRoles, retrieveLeadershipRoles } = useGetLeadershipRoles();
    const VISIBLE_ROWS = 5;

    const [roles, setRoles] = useState([]);
    const [leadershipModal, setLeadershipModal] = useState({
        show: false,
        isCreate: true,
        formData: {}
    });

    useEffect(() => {
        retrieveLeadershipRoles();
    }, []);

    useEffect(() => {
        if (!Array.isArray(leadershipRoles)) {
            setRoles([]);
            return;
        }
        const normalized = leadershipRoles.map(role => ({
            ...role,
            assignedEmployees: Array.isArray(role.assignedEmployees) ? role.assignedEmployees : [],
            assignedClients: Array.isArray(role.assignedClients) ? role.assignedClients : [],
        }));
        setRoles(normalized);
    }, [leadershipRoles]);


    const toggleLeadershipModal = (show, isCreate = true, role = {}) => {
        setLeadershipModal({ show, isCreate, formData: role });
    };

    const createLeadershipRole = async (formData) => {
        try {
            const res = await axios.post(`${API_URL}/chats/leadership/roles`, formData, headers);
            retrieveLeadershipRoles();
            return res.data;
        } catch (err) {
            Toast.error(err.getApiErrorDetails().message);
        } 
    };

    const updateLeadershipRole = async (formData) => {
        try {
            await axios.put(`${API_URL}/chats/leadership/roles/${formData.uuid}`, formData, headers);
            retrieveLeadershipRoles();
        } catch (err) {
            Toast.error(err.getApiErrorDetails().message);
        } finally {
            toggleLeadershipModal(false);
        }
    };

    const deleteLeadershipRole = async (formData) => {
        try {
            await axios.delete(`${API_URL}/chats/leadership/roles/${formData.uuid}`, headers);
            Toast.success("Leadership role has been deleted.");
            retrieveLeadershipRoles();
        } catch (err) {
            Toast.error(err.getApiErrorDetails().message);
        } finally {
            toggleLeadershipModal(false);
        }
    };

    const renderEmptyRows = () => {
        const emptyRows = VISIBLE_ROWS - roles.length;

        if (emptyRows <= 0) return null;

        return Array.from({ length: emptyRows }, (_, index) => (
            <tr key={`empty-row-${index}`}>
                <td>&nbsp;</td>
                <td></td>
                <td></td>
                <td></td>
            </tr>
        ));
    };
  
    const refreshRoles = async () => {
        await retrieveLeadershipRoles();
    };

    return (
        <LeadershipContext.Provider
            value={{
                isFormCreate: leadershipModal.isCreate,
                leadershipRoleToEdit: leadershipModal.formData,
                toggleLeadershipModal,
                createLeadershipRole,
                updateLeadershipRole,
                deleteLeadershipRole
            }}
        >
            <StandardModal
                open={showDialog}
                onClose={handleClose}
                title="Manage Leadership Roles"
                widthMode="compactWide"
                footer={(
                    <StandardModalActionGroup $pushRight>
                        <StandardModalActionButton onClick={handleClose}>Close</StandardModalActionButton>
                    </StandardModalActionGroup>
                )}
            >
                <Box sx={{ flex: 1, overflowY: "auto" }}>
                    <StandardModalSection>
                        <FlexContainer direction="row" marginBottom="0" justifyContent="space-between" style={{ gap: "1rem", flexWrap: "wrap" }}>
                            <StandardModalDescription>
                            Leadership Role Titles appear in the app and web chat menu. When assigned, users are clearly identified as leaders and can be contacted via chat by {customNames.employees.toLowerCase()} or {customNames.clients.toLowerCase()}.
                            </StandardModalDescription>

                            <CreateRoleButton onClick={() => toggleLeadershipModal(true, true)}>
                            Create New Leadership Role
                            </CreateRoleButton>
                        </FlexContainer>
                    </StandardModalSection>

                    <StandardModalTableSection style={{ marginTop: "1rem" }}>
                        <StandardModalTableScroll maxHeight="24rem">
                            <StandardModalTable minWidth="640px">
                                <thead>
                                    <tr>
                                        <th>Leadership Role Name</th>
                                        <th>Assigned {customNames.employees}</th>
                                        <th>Assigned {customNames.clients}</th>
                                        <th style={{ textAlign: "center" }}>View/Edit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {roles.map(role => (
                                        <tr key={role.uuid}>
                                            <td style={{ width: "40%" }}>{role.name}</td>
                                            <td>{role.totalEmployees || 0}</td>
                                            <td>{role.totalClients || 0}</td>
                                            <td style={{ textAlign: "center" }}>
                                                <ViewEditButton
                                                    onClick={() => toggleLeadershipModal(true, false, role)}
                                                > View/Edit
                                                </ViewEditButton>
                                            </td>
                                        </tr>
                                    ))}

                                    {renderEmptyRows()}
                                </tbody>

                            </StandardModalTable>
                        </StandardModalTableScroll>
                    </StandardModalTableSection>
                </Box>

                {/* Create / Edit Modal */}
                {leadershipModal.show && (
                    <LeadershipRoleModal
                        showDialog={leadershipModal.show}
                        handleClose={() => toggleLeadershipModal(false)}
                        token={props.user.token}
                        refetchRoles={refreshRoles}
                    />
                )}
            </StandardModal>
        </LeadershipContext.Provider>
    );
};

export default ManageLeaderShipRoleModal;
